import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FixingScheduleEntry } from '../../types/tradeBooking';
import { businessCalendar } from '../../services/businessCalendar';
import './FixingSchedulePanel.css';

interface FixingSchedulePanelProps {
  schedule: FixingScheduleEntry[];
  tradeDate: string;     // YYYY-MM-DD — fixing dates must be after this
  maturityDate: string;  // YYYY-MM-DD — fixing dates must be before this
  onAddDates: (dates: string[]) => void;
  onRemoveDate: (date: string) => void;
  onClearAll: () => void;
  disabled: boolean;
  minDates?: number;     // Minimum required dates (default 2 for Asiático, 1 for Forward Starting)
  compact?: boolean;     // Compact mode: no header, just calendar icon + date chips
  triggerOnly?: boolean; // Only render calendar trigger + dropdown, no chips/header/warning
}

const DAY_LABELS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

const pad2 = (n: number) => String(n).padStart(2, '0');

const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatDisplay = (dateStr: string, short = false): string => {
  const [y, m, d] = dateStr.split('-');
  return short ? `${d}/${m}` : `${d}/${m}/${y}`;
};

const FixingSchedulePanel: React.FC<FixingSchedulePanelProps> = ({
  schedule,
  tradeDate,
  maturityDate,
  onAddDates,
  onRemoveDate,
  onClearAll,
  disabled,
  minDates = 2,
  compact = false,
  triggerOnly = false,
}) => {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [pendingDates, setPendingDates] = useState<Set<string>>(new Set());
  const [rangeMode, setRangeMode] = useState(true);
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const calRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const committedSet = new Set(schedule.map(e => e.date));

  // Close calendar on outside click
  useEffect(() => {
    if (!calendarOpen) return;
    const handler = (e: MouseEvent) => {
      if (calRef.current && !calRef.current.contains(e.target as Node)) {
        setPendingDates(new Set());
        setRangeStart(null);
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [calendarOpen]);

  // Build calendar grid for the viewed month
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  // Monday=0 based offset
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  /** Generate all business days between two ISO date strings (inclusive) */
  const getBusinessDaysInRange = (from: string, to: string): string[] => {
    const dates: string[] = [];
    const current = new Date(from + 'T00:00:00');
    // Compare date strings, not Date objects — avoids DST clock-shift edge cases
    while (toYMD(current) <= to) {
      const dateStr = toYMD(current);
      const afterTrade = !tradeDate || dateStr > tradeDate;
      const beforeMaturity = !maturityDate || dateStr <= maturityDate;
      if (afterTrade && beforeMaturity && businessCalendar.isBusinessDay(current)) {
        dates.push(dateStr);
      }
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleDayClick = (day: number) => {
    const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;

    if (!rangeMode) {
      // Individual mode — existing toggle behavior
      if (committedSet.has(dateStr)) {
        onRemoveDate(dateStr);
        return;
      }
      setPendingDates(prev => {
        const next = new Set(prev);
        if (next.has(dateStr)) next.delete(dateStr);
        else next.add(dateStr);
        return next;
      });
    } else {
      // Range mode — first click = start, second click = fill range
      if (!rangeStart) {
        setRangeStart(dateStr);
      } else {
        const from = rangeStart < dateStr ? rangeStart : dateStr;
        const to = rangeStart < dateStr ? dateStr : rangeStart;
        const rangeDates = getBusinessDaysInRange(from, to);
        setPendingDates(prev => {
          const next = new Set(prev);
          for (const d of rangeDates) {
            if (!committedSet.has(d)) next.add(d);
          }
          return next;
        });
        setRangeStart(null);
      }
    }
  };

  const handleApply = () => {
    if (pendingDates.size > 0) {
      onAddDates(Array.from(pendingDates));
    }
    setPendingDates(new Set());
    setRangeStart(null);
    setCalendarOpen(false);
  };

  const isAfterMaturity = (dateStr: string): boolean => {
    if (!maturityDate) return false;
    return dateStr > maturityDate;
  };

  const toggleRangeMode = () => {
    setRangeMode(prev => !prev);
    setRangeStart(null);
  };

  const sortedDates = [...schedule].filter(e => e.date).sort((a, b) => a.date.localeCompare(b.date));
  const dateCount = sortedDates.length;

  // Shared calendar trigger + dropdown (used in both triggerOnly and full modes)
  const calendarTriggerEl = (
    <div className="fixing-calendar-wrapper" ref={calRef}>
      <button
        type="button"
        className="fixing-calendar-trigger"
        ref={triggerRef}
        onClick={() => {
          if (disabled) return;
          if (!calendarOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left });
          }
          setCalendarOpen(!calendarOpen);
        }}
        disabled={disabled}
      >
        &#128197;
      </button>

      {calendarOpen && dropdownPos && (
        <div
          className="fixing-calendar-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            margin: 0,
          }}
        >
          {/* Mode toggle */}
          <div className="fixing-cal-mode">
            <button
              type="button"
              className={`fixing-cal-mode-btn ${rangeMode ? 'active' : ''}`}
              onClick={toggleRangeMode}
            >
              {t('tradeBooking.forwardAsiatico.modeRange')}
            </button>
            <button
              type="button"
              className={`fixing-cal-mode-btn ${!rangeMode ? 'active' : ''}`}
              onClick={() => { setRangeMode(false); setRangeStart(null); }}
            >
              {t('tradeBooking.forwardAsiatico.modeIndividual')}
            </button>
          </div>

          <div className="fixing-cal-nav">
            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}>&lsaquo;</button>
            <span>{monthNames[month]} {year}</span>
            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}>&rsaquo;</button>
          </div>
          <div className="fixing-cal-grid">
            {DAY_LABELS.map(d => (
              <div key={d} className="fixing-cal-dayhead">{d}</div>
            ))}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} className="fixing-cal-cell empty" />;
              const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;
              const isCommitted = committedSet.has(dateStr);
              const isPending = pendingDates.has(dateStr);
              const isRangeStart = rangeStart === dateStr;
              const afterMat = isAfterMaturity(dateStr);
              const beforeOrOnTrade = tradeDate ? dateStr <= tradeDate : false;
              const cellDate = new Date(year, month, day);
              const isNonBusinessDay = !businessCalendar.isBusinessDay(cellDate);
              const isDisabled = afterMat || beforeOrOnTrade || isNonBusinessDay;
              const today = toYMD(new Date()) === dateStr;
              return (
                <button
                  key={dateStr}
                  type="button"
                  className={
                    'fixing-cal-cell' +
                    (isCommitted ? ' committed' : '') +
                    (isPending ? ' pending' : '') +
                    (isRangeStart ? ' range-start' : '') +
                    (isDisabled ? ' disabled-day' : '') +
                    (today ? ' today' : '')
                  }
                  onClick={() => handleDayClick(day)}
                  disabled={isDisabled}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Range hint */}
          {rangeMode && rangeStart && (
            <div className="fixing-cal-hint">
              {t('tradeBooking.forwardAsiatico.rangeHint', { date: formatDisplay(rangeStart) })}
            </div>
          )}

          {pendingDates.size > 0 && (
            <button type="button" className="btn-apply-dates" onClick={handleApply}>
              OK ({pendingDates.size})
            </button>
          )}
        </div>
      )}
    </div>
  );

  // triggerOnly mode: just the calendar icon + dropdown, parent handles chips
  if (triggerOnly) {
    return calendarTriggerEl;
  }

  return (
    <div className={`fixing-schedule-panel${compact ? ' compact' : ''}`}>
      {!compact && (
        <div className="fixing-schedule-header">
          <label className="form-label" style={{ marginBottom: 0 }}>
            {t('tradeBooking.forwardAsiatico.fixingSchedule')}
          </label>
          <div className="fixing-schedule-header-right">
            {dateCount > 0 && (
              <>
                <span className="fixing-schedule-count">
                  {t('tradeBooking.forwardAsiatico.datesSelected', { count: dateCount })}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    className="fixing-clear-all"
                    onClick={onClearAll}
                  >
                    {t('tradeBooking.forwardAsiatico.clearAll')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="fixing-schedule-row">
        {calendarTriggerEl}

        {/* Committed date chips */}
        {dateCount > 0 && (
          <div className="fixing-date-chips">
            {sortedDates.map((entry) => (
              <button
                key={entry.date}
                type="button"
                className={`fixing-date-chip ${isAfterMaturity(entry.date) ? 'chip-error' : ''}`}
                onClick={() => !disabled && onRemoveDate(entry.date)}
                disabled={disabled}
                title={t('tradeBooking.forwardAsiatico.removeDate')}
              >
                {formatDisplay(entry.date, compact)}
                <span className="chip-remove">&times;</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {dateCount > 0 && dateCount < minDates && (
        <div className="fixing-schedule-warning">
          {t('tradeBooking.forwardAsiatico.minimumDatesError')}
        </div>
      )}
    </div>
  );
};

export default FixingSchedulePanel;
